
import React, { useEffect, useMemo, useState } from 'react';
import YouTube from 'react-youtube';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import './infovideos.css';

const DEFAULT_USER_GUIDE_URL =
  process.env.REACT_APP_SUPPORT_USER_GUIDE_URL || '/support/usersguide';

const DEFAULT_CONTACT_URL =
  process.env.REACT_APP_SUPPORT_CONTACT_URL || '/support/contact';

function parseVideoIds() {
  const raw = (process.env.REACT_APP_SUPPORT_INFO_VIDEO_IDS || '').trim();
  if (raw) {
    return raw.split(',').map((id) => id.trim()).filter(Boolean);
  }
  return [];
}

function VideoSeriesCaption({ userGuideUrl }) {
  return (
    <Typography
      style={{
        fontFamily: 'DM Sans, sans-serif',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
      }}
    >
      The world of mortgages can be complex and we’re here to help break it down every step of the way and help with our simple online application on{' '}
      <a href={userGuideUrl}>CliQloan</a>.
    </Typography>
  );
}

const InfoVideos = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [failedVideoIds, setFailedVideoIds] = useState(() => new Set());
  const videoIds = useMemo(() => parseVideoIds(), []);
  const userGuideUrl = DEFAULT_USER_GUIDE_URL;
  const contactUrl = DEFAULT_CONTACT_URL;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 960);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const opts = {
    height: '390',
    width: '640',
    playerVars: {
      controls: 1,
      rel: 0,
    },
  };

  const markVideoFailed = (videoId) => {
    setFailedVideoIds((prev) => {
      if (prev.has(videoId)) return prev;
      const next = new Set(prev);
      next.add(videoId);
      return next;
    });
  };

  const playableVideoIds = videoIds.filter((id) => !failedVideoIds.has(id));
  const showUserGuideFallback = playableVideoIds.length === 0;

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mr: isMobile ? 2 : 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontSize: isMobile ? '32px' : '24px', fontWeight: 700, fontFamily: 'Inter' }}
        >
          Informative Video Series
        </Typography>

        {showUserGuideFallback ? (
          <Card sx={{ width: '100%', mt: '1%', mb: '1%' }}>
            <Box p={2}>
              <Box
                sx={{
                  background: '#F0F8FF',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Step-by-step guides are available
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2, maxWidth: 560, mx: 'auto' }}
                >
                  Browse role-based walkthroughs for brokers, MLOs, MLPs, and borrowers in the CliQloan User Guide.
                  You can also contact support if you need help with your account.
                </Typography>
                <Button variant="contained" href={userGuideUrl} sx={{ mr: 1, mb: 1 }}>
                  Open User Guide
                </Button>
                <Button variant="outlined" href={contactUrl} sx={{ mb: 1 }}>
                  Contact Support
                </Button>
              </Box>
            </Box>
            <CardContent>
              <VideoSeriesCaption userGuideUrl={userGuideUrl} />
            </CardContent>
          </Card>
        ) : null}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          {playableVideoIds.map((videoId, index) => (
            <Grid item key={`${videoId}-${index}`} xs={12}>
              <Card sx={{ width: '100%', mt: '1%', mb: '1%' }}>
                <Box px={2} py={2}>
                  <div className="youtubeWrapper">
                    <YouTube
                      videoId={videoId}
                      opts={opts}
                      onError={() => markVideoFailed(videoId)}
                    />
                  </div>
                </Box>
                <CardContent>
                  <VideoSeriesCaption userGuideUrl={userGuideUrl} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InfoVideos;
